/**
 * Ansichten, Textbausteine, Ticketvorlagen und Makros.
 *
 * Vier Bestaende in einer Datei, weil sie dasselbe leisten: Sie ersparen
 * Wiederholung. Vier Dateien mit je fuenf gleichartigen Aufrufen waeren
 * schwerer zu ueberblicken als eine mit zwanzig.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type SavedView = components['schemas']['SavedViewRead'];
export type SavedViewCreate = components['schemas']['SavedViewCreate'];
export type SavedViewUpdate = components['schemas']['SavedViewUpdate'];

export type Snippet = components['schemas']['TextSnippetRead'];
export type SnippetCreate = components['schemas']['TextSnippetCreate'];
export type SnippetUpdate = components['schemas']['TextSnippetUpdate'];
export type SnippetRenderResult = components['schemas']['SnippetRenderResult'];
export type Placeholder = components['schemas']['PlaceholderRead'];

export type Template = components['schemas']['TemplateRead'];
export type TemplateCreate = components['schemas']['TemplateCreate'];
export type TemplateUpdate = components['schemas']['TemplateUpdate'];

export type Macro = components['schemas']['MacroRead'];
export type MacroCreate = components['schemas']['MacroCreate'];
export type MacroUpdate = components['schemas']['MacroUpdate'];
export type MacroAction = components['schemas']['MacroActionModel'];
export type MacroActionType = components['schemas']['MacroActionType'];
export type MacroApplyResult = components['schemas']['MacroApplyResult'];

// -- Gespeicherte Ansichten ---------------------------------------------------

export function listSavedViews(): Promise<SavedView[]> {
  return request<SavedView[]>('/saved-views');
}

export function createSavedView(payload: SavedViewCreate): Promise<SavedView> {
  return request<SavedView>('/saved-views', { method: 'POST', body: payload });
}

export function updateSavedView(viewId: string, payload: SavedViewUpdate): Promise<SavedView> {
  return request<SavedView>(`/saved-views/${viewId}`, { method: 'PATCH', body: payload });
}

export function deleteSavedView(viewId: string): Promise<undefined> {
  return request<undefined>(`/saved-views/${viewId}`, { method: 'DELETE' });
}

// -- Textbausteine ------------------------------------------------------------

export function listSnippets(): Promise<Snippet[]> {
  return request<Snippet[]>('/text-snippets');
}

export function listPlaceholders(): Promise<Placeholder[]> {
  return request<Placeholder[]>('/text-snippets/placeholders');
}

export function createSnippet(payload: SnippetCreate): Promise<Snippet> {
  return request<Snippet>('/text-snippets', { method: 'POST', body: payload });
}

export function updateSnippet(snippetId: string, payload: SnippetUpdate): Promise<Snippet> {
  return request<Snippet>(`/text-snippets/${snippetId}`, { method: 'PATCH', body: payload });
}

export function deleteSnippet(snippetId: string): Promise<undefined> {
  return request<undefined>(`/text-snippets/${snippetId}`, { method: 'DELETE' });
}

/**
 * Setzt einen Baustein ein.
 *
 * Die Ersetzung laeuft auf dem Server. Sie im Browser zu machen hiesse, die
 * Kundendaten fuer die Platzhalter erst dorthin zu holen -- und die Liste der
 * verfuegbaren Felder an zwei Stellen zu pflegen.
 */
export function renderSnippet(
  snippetId: string,
  ticketId: string | null,
): Promise<SnippetRenderResult> {
  return request<SnippetRenderResult>(`/text-snippets/${snippetId}/render`, {
    method: 'POST',
    body: { ticket_id: ticketId },
  });
}

// -- Ticketvorlagen -----------------------------------------------------------

export function listTemplates(): Promise<Template[]> {
  return request<Template[]>('/ticket-templates');
}

export function createTemplate(payload: TemplateCreate): Promise<Template> {
  return request<Template>('/ticket-templates', { method: 'POST', body: payload });
}

export function updateTemplate(templateId: string, payload: TemplateUpdate): Promise<Template> {
  return request<Template>(`/ticket-templates/${templateId}`, { method: 'PATCH', body: payload });
}

export function deleteTemplate(templateId: string): Promise<undefined> {
  return request<undefined>(`/ticket-templates/${templateId}`, { method: 'DELETE' });
}

// -- Makros -------------------------------------------------------------------

export function listMacros(): Promise<Macro[]> {
  return request<Macro[]>('/macros');
}

export function createMacro(payload: MacroCreate): Promise<Macro> {
  return request<Macro>('/macros', { method: 'POST', body: payload });
}

export function updateMacro(macroId: string, payload: MacroUpdate): Promise<Macro> {
  return request<Macro>(`/macros/${macroId}`, { method: 'PATCH', body: payload });
}

export function deleteMacro(macroId: string): Promise<undefined> {
  return request<undefined>(`/macros/${macroId}`, { method: 'DELETE' });
}

export function applyMacro(ticketId: string, macroId: string): Promise<MacroApplyResult> {
  return request<MacroApplyResult>(`/tickets/${ticketId}/macros/${macroId}`, { method: 'POST' });
}
